import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Smartphone, Laptop, Monitor, Tablet, ShieldCheck, AlertTriangle, Loader2, X } from "lucide-react";
import { puvioService } from "../genesis/identity/puvioService";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";

function DeviceIcon({ platform, size = 18 }) {
  const p = (platform || "").toLowerCase();
  if (p.includes("ios") || p.includes("android")) return <Smartphone size={size} />;
  if (p.includes("ipad")) return <Tablet size={size} />;
  if (p.includes("mac") || p.includes("windows")) return <Laptop size={size} />;
  return <Monitor size={size} />;
}

export default function DevicesSheet({ open, onClose }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setDevices(await puvioService.listDevices() || []); }
    catch (e) { toast.error(e?.message || "Couldn't load devices"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const revoke = async (id) => {
    setRevoking(id);
    try {
      await puvioService.revokeDevice(id);
      toast.success("Device signed out");
      load();
    } catch (e) {
      toast.error(e?.message || "Couldn't revoke device");
    } finally { setRevoking(null); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <SheetContent side="bottom" className="rounded-t-[28px] border-0 bg-card max-h-[85vh] overflow-y-auto" data-testid="devices-sheet">
        <SheetHeader className="text-left pb-2">
          <div className="inline-flex w-fit items-center gap-1.5 bg-primary/10 text-primary rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider mb-2">
            <ShieldCheck size={11} /> Device management
          </div>
          <SheetTitle className="text-[26px] font-extrabold tracking-tight">Your devices</SheetTitle>
          <SheetDescription className="text-[13px] text-muted-foreground font-medium">
            See every device signed into your Puvio ID. Revoke any that don't look familiar.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-3 space-y-2" data-testid="devices-list">
          {loading && (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 size={18} className="animate-spin" />
            </div>
          )}
          {!loading && devices.length === 0 && (
            <div className="rounded-2xl border border-border bg-muted/30 p-6 text-center">
              <AlertTriangle size={20} className="mx-auto text-muted-foreground" />
              <p className="text-[13px] text-muted-foreground mt-2 font-medium">No devices on file yet. They'll appear after your next sign-in.</p>
            </div>
          )}
          {!loading && devices.map((d) => (
            <div key={d.device_id} className="rounded-2xl border border-border bg-card p-3.5 flex items-center gap-3" data-testid={`device-row-${d.device_id}`}>
              <span className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center text-foreground/70">
                <DeviceIcon platform={d.platform} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-bold flex items-center gap-1.5 truncate">
                  {d.name || "Unknown device"}
                  {d.current && <span className="text-[9px] font-extrabold uppercase tracking-wider bg-primary/15 text-primary rounded-full px-1.5 py-0.5">This device</span>}
                </p>
                <p className="text-[11px] text-muted-foreground truncate">{d.platform || "Unknown"} · last seen {d.last_seen ? fmtDate(d.last_seen, "MMM d, p") : "recently"}</p>
              </div>
              {!d.current && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:bg-destructive/10 rounded-full h-8 px-3"
                  onClick={() => revoke(d.device_id)}
                  disabled={revoking === d.device_id}
                  data-testid={`device-revoke-${d.device_id}`}
                >
                  {revoking === d.device_id ? <Loader2 size={14} className="animate-spin" /> : <><X size={14} className="mr-1" /> Revoke</>}
                </Button>
              )}
            </div>
          ))}
        </div>

        <p className="text-[11px] text-muted-foreground text-center mt-4 font-medium">
          Sessions live for up to 7 days. Revoke any device to sign it out immediately.
        </p>
      </SheetContent>
    </Sheet>
  );
}
